import { Controller } from "@hotwired/stimulus"
import { install, uninstall } from "@github/hotkey"
import { APPLE_PLATFORM } from "../helpers/platform_keys"

// Registers the element's `data-hotkey` binding with @github/hotkey.
//
// The binding lives on the element it activates (a nav link, a dialog trigger, the
// board's Rückgängig button), so a shortcut only exists when the element itself is
// rendered — permission and tenant-module gating in the view applies to the shortcut
// for free.
//
// Usage: data-controller="hotkey" data-hotkey="g s"
//
// Sequences are space separated ("g s" = press g, then s within 1.5s). Chords use `+`
// ("Mod+z"), where `Mod` is Cmd on Apple and Ctrl everywhere else. The library ignores
// keystrokes originating in form fields, so bindings never fire while the user is typing.

// Shortcuts are OFF until the account switches them on in the Tastaturkürzel dialog.
// The value is persisted on the account (accounts.preferences) and rendered onto the
// cheat-sheet dialog, which every sidebar layout emits — one element carries both
// halves of the wiring, so a layout cannot end up with shortcuts but no cheat sheet.
const ENABLED_ATTRIBUTE = "data-hotkeys-enabled"

// Second, independent flag: shortcuts on, key chips off. Read the same way and from the
// same element, so both survive (and are stripped from) a Turbo snapshot together.
const HINTS_ATTRIBUTE = "data-hotkeys-hints-enabled"

// The last values a server actually rendered. Turbo keeps this module loaded across
// visits, so they stay current even while cached markup does not.
const lastRendered = { [ENABLED_ATTRIBUTE]: false, [HINTS_ATTRIBUTE]: true }

function dialog() {
  return document.getElementById("hotkeys-dialog")
}

function flag(attribute) {
  const rendered = dialog()?.getAttribute(attribute)

  // Absent means this DOM came back out of Turbo's cache with the flag stripped (see
  // below): its value froze when the page was cached and may since have been switched
  // the other way, so the in-memory one is the account's actual setting.
  if (rendered == null) return lastRendered[attribute]

  lastRendered[attribute] = rendered === "true"
  return lastRendered[attribute]
}

export function hotkeysEnabled() {
  return flag(ENABLED_ATTRIBUTE)
}

export function hotkeyHintsEnabled() {
  return flag(HINTS_ATTRIBUTE)
}

// Apple's modifier glyphs are not translated, the words are — those come off the dialog,
// which the server renders, so no German lives in here. See _hotkeys_dialog.html.erb.
function keyLabel(key) {
  if (key === "Mod") return APPLE_PLATFORM ? "⌘" : (dialog()?.dataset.hotkeysModLabel ?? key)
  if (key === "Shift") return APPLE_PLATFORM ? "⇧" : (dialog()?.dataset.hotkeysShiftLabel ?? key)

  return key.toUpperCase()
}

// Splits a `data-hotkey` value into its steps, each step a list of keys pressed together:
// "g s" is [[G], [S]], "Mod+Shift+Z" is [[⌘, ⇧, Z]].
//
// Shared by the cheat sheet and by the tooltips below so a shortcut can never be spelled
// two different ways on the same page.
export function hotkeySteps(sequence) {
  // "Shift+?,?" is one shortcut with two spellings, not two shortcuts — the browser
  // reports Shift for `?` on some layouts and not others. Only the first is shown.
  return sequence
    .split(",")[0]
    .trim()
    .split(" ")
    .map((chord) => {
      const keys = chord.split("+")

      // A lone `Shift+` is that spelling artefact rather than something the planner
      // presses deliberately, so it is dropped. In a real chord (Mod+Shift+Z) it is
      // part of what you press and stays.
      return (keys.length === 2 && keys[0] === "Shift" ? keys.slice(1) : keys).map(keyLabel)
    })
}

// One-line spelling for a tooltip: keys pressed together are joined the way the platform
// writes them (⌘⇧Z on Apple, Strg+Umschalt+Z elsewhere), steps of a sequence by a space.
export function formatHotkey(sequence) {
  return hotkeySteps(sequence)
    .map((keys) => keys.join(APPLE_PLATFORM ? "" : "+"))
    .join(" ")
}

// Turbo caches the page on the way out and restores it verbatim on Back/Forward — flags,
// hint classes and all. An account that switched shortcuts off would keep firing them on
// every restored page, and one that switched them on would find them dead there. Strip
// both marks from the snapshot so a restored page asserts nothing and the flag readers
// fall through to the values held in memory.
document.addEventListener("turbo:before-cache", () => {
  dialog()?.removeAttribute(ENABLED_ATTRIBUTE)
  dialog()?.removeAttribute(HINTS_ATTRIBUTE)

  for (const hint of document.querySelectorAll(".hotkey-hint-visible")) {
    hint.classList.remove("hotkey-hint-visible")
  }
})

// A modal <dialog> makes the rest of the page inert for clicks but not for
// @github/hotkey, whose keydown listener sits on `document` and fires bindings on the
// occluded page underneath. Its isFormField() only skips inputs/textarea/select, so
// focus on a dialog button does not stop it, and `g s` read off the open cheat sheet
// would navigate away from it. The library dispatches a cancelable `hotkey-fire` on the
// bound element before clicking it — cancel that unless the element is inside the open
// dialog. Guards every modal, not just the cheat sheet.
//
// Capture phase is required: `hotkey-fire` is dispatched without `bubbles`, so a
// document listener on the bubble phase would never run. Capturing still does.
document.addEventListener(
  "hotkey-fire",
  (event) => {
    const openDialog = document.querySelector("dialog[open]")

    if (openDialog && !openDialog.contains(event.target)) event.preventDefault()
  },
  true,
)

export default class extends Controller {
  connect() {
    const enabled = hotkeysEnabled()

    // Key hints are hidden by default (see .hotkey-hint in application.css), so the
    // sidebar never advertises a shortcut that is switched off. Toggled rather than
    // added: a restored snapshot has the visible class baked in and must lose it.
    this.element
      .querySelector(".hotkey-hint")
      ?.classList.toggle("hotkey-hint-visible", enabled && hotkeyHintsEnabled())

    this.applyTitle(enabled)

    if (!enabled) return

    install(this.element)
    this.installed = true
  }

  disconnect() {
    if (!this.installed) return

    uninstall(this.element)
    this.installed = false
  }

  // Names the shortcut in the tooltip of an element that already has one (the board's
  // icon-only undo/redo buttons — a nav link has no title and is left alone).
  //
  // Done here rather than in the view because the key names depend on the platform. It
  // rebuilds the whole title from `data-hotkey-label` instead of appending to what is
  // there, so running again over a restored snapshot is a no-op rather than a second
  // suffix. Unlike the chips this follows the shortcuts flag alone: a tooltip is read
  // deliberately, so it is an answer to a question, not clutter to be switched off.
  applyTitle(enabled) {
    const label = this.element.dataset.hotkeyLabel

    if (!label || !this.element.hasAttribute("title")) return

    const title = enabled ? `${label} (${formatHotkey(this.element.dataset.hotkey)})` : label

    this.element.setAttribute("title", title)
    this.element.setAttribute("aria-label", title)
  }
}
